'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';
import PageAtmosphere from '@/components/PageAtmosphere';
import { useT } from '@/lib/useT';
import { Check, Sparkles, Users, Truck } from 'lucide-react';

// Palette bound to the global --c-* theme tokens so pricing honours day/night
// (set via <html data-theme>). Accent stays brand-yellow in both themes.
const V2 = {
  bg:           'var(--c-bg)',
  bg2:          'var(--c-panel)',
  bg3:          'var(--c-card)',
  text:         'var(--c-text)',
  heading:      'var(--c-heading)',
  textMute:     'var(--c-muted)',
  line:         'var(--c-border)',
  accent:       'var(--accent)',
  accentInk:    'var(--accent-ink)',
  accentSoft:   'var(--accent-soft)',
  accentBorder: 'color-mix(in srgb, var(--accent) 20%, transparent)',
  success:      'var(--live)',
};

interface PublicPlan {
  plan: string;
  name?: string;
  priceLabel: string;
  accentColor?: string;
  popular?: boolean;
  description?: string;
  drivers: string;
  vehicles: string;
  features: string[];
}

// Fallback used while the API call is in flight or fails. priceLabel matches
// what /api/public/plans returns; accentColor stays muted (real attention
// is the yellow popular ribbon).
const FALLBACK_PRICING: PublicPlan[] = [
  { plan: 'DRIVER',     priceLabel: '0',           drivers: '1',  vehicles: '1',  popular: false,
    features: ['pf_gps_rt', 'pf_eu561', 'pf_tg_bot', 'pf_sos', 'pf_shift', 'pf_photo'] },
  { plan: 'BASIC',      priceLabel: '49',          drivers: '3',  vehicles: '3',  popular: false,
    features: ['pf_gps_rt', 'pf_eu561', 'pf_tg_bot', 'pf_sos', 'pf_customer_portal', 'pf_shift'] },
  { plan: 'STARTER',    priceLabel: '199',         drivers: '15', vehicles: '15', popular: false,
    features: ['pf_all_basic', 'pf_orders_cmr', 'pf_photo_sig', 'pf_payroll', 'pf_barcode', 'pf_route_opt', 'pf_tg_push', 'pf_email_support'] },
  { plan: 'BUSINESS',   priceLabel: '499',         drivers: '50', vehicles: '50', popular: true,
    features: ['pf_all_starter', 'pf_ai_ocr', 'pf_geofencing', 'pf_tacho_eu', 'pf_marketplace', 'pf_tco_forecast', 'pf_ai_copilot', 'pf_esg', 'pf_stripe_links', 'pf_prio_support'] },
  { plan: 'ENTERPRISE', priceLabel: 'Auf Anfrage', drivers: '∞',  vehicles: '∞',  popular: false,
    features: ['pf_all_business', 'pf_whitelabel_sso', 'pf_api_webhooks', 'pf_multi_depot', 'pf_audit', 'pf_account_mgr', 'pf_sla_onprem'] },
];

const RAW_API = process.env.NEXT_PUBLIC_API_URL || '';
const API = RAW_API.replace(/\/api$/, '');

export default function PricingClient() {
  const t = useT();
  const [pricing, setPricing] = useState<PublicPlan[]>(FALLBACK_PRICING);

  useEffect(() => {
    fetch(`${API}/api/public/plans`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (Array.isArray(data) && data.length > 0) setPricing(data); })
      .catch(() => {/* keep fallback */});
  }, []);

  return (
    <>
      <PublicHeader />
      <PageAtmosphere />
      <div className="min-h-screen" style={{ background: V2.bg, color: V2.text }}>

        {/* ════════════ PAGE TITLE ════════════ */}
        <section className="relative overflow-hidden px-6 md:px-8 pt-20 pb-12 max-w-7xl mx-auto text-center">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, color-mix(in srgb, var(--accent) 8%, transparent) 0%, transparent 70%)' }} />

          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 text-xs font-semibold tracking-wide uppercase"
              style={{ background: V2.accentSoft, color: V2.accent, border: `1px solid ${V2.accentBorder}` }}>
              <Sparkles size={14} /> {t('pub_nav_pricing')}
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4" style={{ color: V2.heading }}>
              {t('home_pricing_title')}
            </h1>
            <p className="text-base md:text-lg max-w-xl mx-auto" style={{ color: V2.textMute }}>
              {t('home_pricing_sub')}
            </p>
          </div>
        </section>

        {/* ════════════ PRICING GRID ════════════ */}
        <section className="px-6 md:px-8 pb-20 max-w-7xl mx-auto">
          <style>{`
            .lv3pc { transition: transform .2s ease, border-color .2s ease, box-shadow .2s ease; }
            .lv3pc:hover { transform: translateY(-5px); box-shadow: 0 18px 50px color-mix(in srgb, var(--accent) 14%, transparent); }
            .lv3pc-pri { transition: transform .16s ease, box-shadow .16s ease, background .16s; }
            .lv3pc-pri:hover { transform: translateY(-2px); box-shadow: 0 10px 28px color-mix(in srgb, var(--accent) 30%, transparent); }
            .lv3pc-gho { transition: transform .16s ease, border-color .16s, color .16s; }
            .lv3pc-gho:hover { transform: translateY(-2px); border-color: var(--accent) !important; color: var(--accent) !important; }
          `}</style>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {pricing.map((p, i) => {
              const priceLabel = p.priceLabel;
              const isCustomPrice = priceLabel === 'Auf Anfrage' || priceLabel === 'On request';
              const isFree = p.plan === 'DRIVER';
              const isPopular = !!p.popular;

              const isInheritKey = (s: string) => s?.startsWith('pf_all_');
              const inheritLine = isInheritKey(p.features[0]) ? p.features[0] : null;
              const featureItems: string[] = inheritLine ? p.features.slice(1) : p.features;

              return (
                <div
                  key={p.plan || i}
                  className="lv3pc relative flex flex-col"
                  style={{
                    background: 'var(--c-card)',
                    border: `1px solid ${isPopular ? V2.accent : 'var(--c-border)'}`,
                    borderRadius: 18,
                    padding: '26px 22px',
                    boxShadow: isPopular ? '0 0 0 1px color-mix(in srgb, var(--accent) 30%, transparent), 0 16px 48px color-mix(in srgb, var(--accent) 10%, transparent)' : undefined,
                    overflow: 'hidden',
                  }}
                >
                  {isPopular && <span aria-hidden style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 3, background: V2.accent }} />}
                  {isPopular && (
                    <span
                      className="absolute top-4 right-4 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase"
                      style={{ background: V2.accent, color: V2.accentInk, letterSpacing: '0.08em', fontFamily: 'JetBrains Mono, monospace' }}
                    >
                      {t('home_price_popular')}
                    </span>
                  )}

                  <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: V2.accent, textTransform: 'uppercase', marginBottom: 8 }}>
                    {String(i + 1).padStart(2, '0')} · {p.plan}
                  </div>
                  <h3 style={{ fontFamily: 'Space Grotesk, Inter, sans-serif', fontSize: 21, fontWeight: 600, color: 'var(--c-heading)', letterSpacing: '-0.01em', margin: '0 0 6px' }}>
                    {p.name || t(`plan_name_${p.plan.toLowerCase()}`)}
                  </h3>
                  <p style={{ color: V2.textMute, fontSize: 13, lineHeight: 1.5, margin: '0 0 18px' }}>
                    {p.description || t(`plan_desc_${p.plan.toLowerCase()}`)}
                  </p>

                  <div style={{ marginBottom: 18, paddingBottom: 18, borderBottom: `1px solid var(--c-border)` }}>
                    {isCustomPrice ? (
                      <div>
                        <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 32, fontWeight: 700, color: V2.accent }}>
                          {t('home_price_custom')}
                        </span>
                        <p style={{ marginTop: 4, fontSize: 12, color: V2.textMute }}>
                          {t('home_price_enterprise_hint')}
                        </p>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                        <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: 54, color: V2.accent, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                          {priceLabel}
                        </span>
                        <span style={{ fontSize: 18, fontWeight: 700, color: V2.accent }}>€</span>
                        <span style={{ fontSize: 13, color: V2.textMute }}>/ {t('home_price_per_month')}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 14, marginTop: 12, fontSize: 12, color: V2.textMute, fontFamily: 'JetBrains Mono, monospace' }}>
                      <span className="inline-flex items-center gap-1">
                        <Users size={11} />
                        {p.drivers === 'Unbegrenzt' ? '∞' : p.drivers} {t('home_price_drivers')}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Truck size={11} />
                        {p.vehicles === 'Unbegrenzt' ? '∞' : p.vehicles} {t('home_price_vehicles')}
                      </span>
                    </div>
                  </div>

                  <div className="flex-1 space-y-2">
                    {inheritLine && (
                      <div
                        className="flex items-center gap-2 mb-2 px-3 py-1.5 rounded-lg"
                        style={{ background: V2.accentSoft, border: `1px solid ${V2.accentBorder}` }}
                      >
                        <Sparkles size={11} style={{ color: V2.accent }} />
                        <span style={{ fontSize: 11, fontWeight: 700, color: V2.accent, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.04em' }}>
                          {t(inheritLine)}
                        </span>
                      </div>
                    )}
                    {featureItems.map((f, j) => (
                      <div key={j} className="flex items-start gap-2" style={{ color: V2.text, fontSize: 13, lineHeight: 1.5 }}>
                        <Check size={14} style={{ color: V2.accent, marginTop: 3, flexShrink: 0 }} strokeWidth={2.6} />
                        <span>{t(f)}</span>
                      </div>
                    ))}
                  </div>

                  <Link
                    prefetch={false}
                    href={isCustomPrice ? '/kontakt' : isFree ? '/register/driver' : '/register'}
                    className={`mt-6 w-full inline-flex items-center justify-center py-3 text-sm font-semibold ${isPopular ? 'lv3pc-pri' : 'lv3pc-gho'}`}
                    style={
                      isPopular
                        ? { background: V2.accent, color: V2.accentInk, borderRadius: 12 }
                        : { background: 'transparent', color: V2.text, border: `1px solid var(--c-border2)`, borderRadius: 12 }
                    }
                  >
                    {isCustomPrice
                      ? t('home_price_contact')
                      : isFree
                      ? t('home_price_driver_cta')
                      : isPopular
                      ? t('home_price_start')
                      : t('home_price_choose')}
                  </Link>
                </div>
              );
            })}
          </div>
        </section>

      </div>
      <PublicFooter />
    </>
  );
}
